import React, { memo, useEffect, useRef, useState } from 'react'
import { isEqual } from 'lodash'
import { Container, ListWithVisibleItems, ContainerWithListHeight } from './styles'
import { CardHealthProcedure } from '../CardHealthProcedure'
import { AlertMessage } from '~/components/shared'
import { IHealthProcedures } from '~/interfaces'
import { message } from '~/services'

export interface ListCardHealthProcedureProps {
  list: IHealthProcedures[]
  onSelectCard: (healthProcedure: IHealthProcedures) => void
}

interface IVisibleLines {
  start: number
  end: number
}

const ITEM_HEIGHT = 69
const ITEM_GAP = 16
const EXTRA_LINES = 2
const ITEM_MIN_WIDTH = 233 + ITEM_GAP

const MIN_START_LINE = 0
const MIN_END_LINE = 8

const ListCardHealthProcedure = memo(
  ({ list, onSelectCard }: ListCardHealthProcedureProps): JSX.Element => {
    const mainContainerRef = useRef<HTMLDivElement>(null)
    const [visibleLines, setVisibleLines] = useState<IVisibleLines>({ start: MIN_START_LINE, end: MIN_END_LINE })

    const lineHeight = ITEM_HEIGHT + ITEM_GAP

    const quantityOfCardsPerLine =
      Math.floor((Number(mainContainerRef?.current?.offsetWidth) - 4) / ITEM_MIN_WIDTH) || 1
    const listTotalHeight = Math.ceil(list.length / quantityOfCardsPerLine) * lineHeight + EXTRA_LINES

    useEffect(() => {
      const resetScrollToTop = () => {
        if (mainContainerRef?.current && mainContainerRef?.current?.scrollTo) {
          mainContainerRef.current.scrollTo(0, 0)
        }
      }

      resetScrollToTop()
    }, [list])

    const handleOnClick = (healthProcedure: IHealthProcedures): void => {
      onSelectCard(healthProcedure)
    }

    const countLinesToRender = (event: React.UIEvent<HTMLDivElement, UIEvent>): void => {
      const firstLineToRender = Math.floor(event.currentTarget.scrollTop / lineHeight)
      const isStartLineDifferentThanCurrentOne = firstLineToRender !== visibleLines.start

      if (isStartLineDifferentThanCurrentOne) {
        const listHeight = event.currentTarget.offsetHeight
        const finalLineToRender = Math.floor((event.currentTarget.scrollTop + listHeight) / lineHeight)

        setVisibleLines({ end: finalLineToRender, start: firstLineToRender })
      }
    }

    const getVisibleLines = (): IHealthProcedures[] => {
      const { start, end } = visibleLines
      const startLineItemIndex = start * quantityOfCardsPerLine
      const endLineItemIndex = (end + EXTRA_LINES) * quantityOfCardsPerLine

      return list.slice(startLineItemIndex, endLineItemIndex)
    }

    return (
      <Container ref={mainContainerRef} onScroll={countLinesToRender}>
        {list.length > 0 ? (
          <ContainerWithListHeight height={listTotalHeight}>
            <ListWithVisibleItems scrollPosition={visibleLines.start * lineHeight} data-testid="cardProcedureList">
              {getVisibleLines().map(healthProcedure => (
                <CardHealthProcedure
                  key={healthProcedure.id}
                  healthProcedure={healthProcedure}
                  onClick={handleOnClick}
                />
              ))}
            </ListWithVisibleItems>
          </ContainerWithListHeight>
        ) : (
          <AlertMessage>{message('healthProcedures.empty_list')}</AlertMessage>
        )}
      </Container>
    )
  },
  (prevProps, nextProps) => isEqual(prevProps.list, nextProps.list)
)

ListCardHealthProcedure.displayName = 'ListCardHealthProcedure'

export { ListCardHealthProcedure }
